// import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// import axios from "axios";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// const apiBaseUrl = process.env.REACT_APP_API_URL;

export interface HolidayRequestDto {
  id?: number;
  sessionId: number;
  typeId: number;
  dates: string[];
  forAllClasses: boolean;
  description: string;
  classIds?: number[];
}

export interface HolidayResponseDto {
  id: number;
  sessionId: number;
  typeId: number;
  typeName: string;
  typeDescription: string;
  dates: string[];
  forAllClasses: boolean;
  description: string;
  classIds?: number[];
  createdAt: string;
}

export interface HolidayTypeRequestDto {
  name: string;
  description: string;
  active: boolean;
}

export interface HolidayTypeResponseDto {
  id: number;
  name: string;
  description: string;
  active: boolean;
  createdAt: string;
}

const Holiday_API_URL = `${process.env.REACT_APP_API_URL}/holiday`;
const Holiday_TYPE_API_URL = `${process.env.REACT_APP_API_URL}/holiday-type`;

export const getHolidaysBySession = async (
  sessionId: number
): Promise<HolidayResponseDto[]> => {
  const response = await axios.get(
    `${Holiday_API_URL}/session/${sessionId}`
  );
  return response.data;
};

export const getHolidayBySessionAndClass = async (
  sessionId: number,
  classId: number
): Promise<HolidayResponseDto[]> => {
  const response = await axios.get(
    `${Holiday_API_URL}/session/${sessionId}/class/${classId}`
  );
  return response.data;
};

export const createHoliday = async (
  data: HolidayRequestDto
): Promise<HolidayResponseDto> => {
  const response = await axios.post(`${Holiday_API_URL}`, data);
  return response.data;
};

export const deleteHoliday = async (
  holidayId: number
): Promise<void> => {
  await axios.delete(`${Holiday_API_URL}/${holidayId}`);
};

export const createHolidayType = async (
  data: HolidayTypeRequestDto
): Promise<HolidayTypeResponseDto> => {
  const response = await axios.post(Holiday_TYPE_API_URL, data);
  return response.data;
};

export const getActiveHolidayTypes = async (): Promise<
  HolidayTypeResponseDto[]
> => {
  const response = await axios.get(`${Holiday_TYPE_API_URL}/active`);
  return response.data;
};

export const useHolidays = (sessionId?: number) => {

  const queryClient = useQueryClient();

  const useholidaysQueryBySession = useQuery<HolidayResponseDto[]>({
    queryKey: ["holidays", sessionId],
    queryFn: () => {
      if (!sessionId) return Promise.resolve([]);
      return getHolidaysBySession(sessionId);
    },
    enabled: !!sessionId,
  });

  const createHolidayMutation = useMutation({
    mutationFn: createHoliday,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["holidays", sessionId] });
    },
  });

  const deleteHolidayMutation = useMutation({
    mutationFn: deleteHoliday,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["holidays", sessionId] });
    },
  });

  const createHolidayTypeMutation = useMutation({
    mutationFn: createHolidayType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["holiday-types"] });
    },
  });

  const activeHolidayTypesQuery = useQuery({
    queryKey: ["holiday-types", "active"],
    queryFn: getActiveHolidayTypes,
    staleTime: 5 * 60 * 1000,
  });

  return {
    useholidaysQueryBySession,
    createHolidayMutation,
    deleteHolidayMutation,
    createHolidayTypeMutation,
    activeHolidayTypesQuery
  };
};
